import React, { useEffect, useState } from 'react';
import api from '../utils/api';

const GRADE_COLORS = { A: 'var(--neon-green)', B: 'var(--neon-cyan)', C: 'var(--neon-yellow)', D: 'var(--neon-pink)' };
const VISIBILITY_COLORS = { full: 'var(--neon-green)', partial: 'var(--neon-yellow)', none: 'var(--muted2)' };
const Q_STATUS_COLORS = { completed: 'var(--neon-green)', sent: 'var(--neon-yellow)', pending: 'var(--muted2)' };

export default function SupplyChain() {
  const [summary, setSummary] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingQ, setSendingQ] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    supplier_name: '', category: 'raw_materials', annual_spend_inr: '', contact_email: '',
    estimated_scope3_tco2e: '', emissions_score: '50', scope3_visibility: 'none',
  });

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/supply-chain/scope3-summary').then((r) => setSummary(r.data)).catch(() => {}),
      api.get('/supply-chain/suppliers').then((r) => setSuppliers(r.data.suppliers || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSendQuestionnaire = async (supplierId) => {
    setSendingQ(supplierId);
    try {
      await api.post('/supply-chain/questionnaire', { supplier_id: supplierId });
      load();
    } catch {}
    setSendingQ(null);
  };

  const handleAddSupplier = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    setSubmitting(true);
    try {
      await api.post('/supply-chain/suppliers', {
        ...form,
        annual_spend_inr: parseFloat(form.annual_spend_inr) || 0,
        estimated_scope3_tco2e: parseFloat(form.estimated_scope3_tco2e) || 0,
        emissions_score: parseInt(form.emissions_score, 10),
      });
      setSuccess('Supplier added successfully.');
      setShowAddForm(false);
      setForm({ supplier_name: '', category: 'raw_materials', annual_spend_inr: '', contact_email: '', estimated_scope3_tco2e: '', emissions_score: '50', scope3_visibility: 'none' });
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add supplier.');
    } finally {
      setSubmitting(false);
    }
  };

  const questionnairesCompleted = summary?.questionnaires_completed || 0;
  const questionnairesTotal = summary?.questionnaires_total || 0;
  const progressPct = questionnairesTotal > 0 ? Math.round((questionnairesCompleted / questionnairesTotal) * 100) : 0;

  return (
    <div className="page-shell page-stack">
      <section className="section-stack">
        <div className="section-tag">Supplier Insights — Supply Chain</div>
        <h1>Scope 3 Supplier Engagement</h1>
        <p className="section-copy">Scope 3 typically accounts for 70%+ of a company's total emissions. Engage your top suppliers to measure and reduce your value chain footprint.</p>
      </section>

      {/* ── Scope 3 Summary ── */}
      {summary && (
        <section className="surface-panel glow-surface" style={{ padding: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1.5rem' }}>
            <div>
              <div className="stat-label">Total Scope 3 from suppliers</div>
              <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '1.6rem', color: 'var(--neon-cyan)', marginTop: '0.25rem' }}>
                {summary.total_scope3_tco2e} <span style={{ fontSize: '0.8rem', color: 'var(--muted2)' }}>tCO2e</span>
              </div>
            </div>
            <div>
              <div className="stat-label">Avg supplier emissions grade</div>
              <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '1.6rem', color: GRADE_COLORS[summary.avg_emissions_grade] || 'var(--muted2)', marginTop: '0.25rem' }}>
                Grade {summary.avg_emissions_grade || '?'}
              </div>
            </div>
            <div>
              <div className="stat-label">Questionnaire completion</div>
              <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '1.6rem', color: progressPct >= 80 ? 'var(--neon-green)' : 'var(--neon-yellow)', marginTop: '0.25rem' }}>
                {questionnairesCompleted}/{questionnairesTotal}
              </div>
              <div style={{ marginTop: '0.5rem', height: '4px', borderRadius: '2px', background: 'var(--border)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${progressPct}%`, background: progressPct >= 80 ? 'var(--neon-green)' : 'var(--neon-yellow)', borderRadius: '2px' }} />
              </div>
            </div>
            <div>
              <div className="stat-label">Suppliers tracked</div>
              <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '1.6rem', color: 'var(--neon-green)', marginTop: '0.25rem' }}>
                {summary.supplier_count}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Supplier Scorecard Table ── */}
      <section className="surface-panel" style={{ padding: '1.5rem' }}>
        <div className="section-header-row" style={{ marginBottom: '1.25rem' }}>
          <div className="section-stack">
            <div className="section-tag">Supplier scorecard</div>
            <h2 style={{ fontSize: '1.1rem' }}>Ranked by Scope 3 emissions</h2>
          </div>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowAddForm((v) => !v)}>
            {showAddForm ? 'Cancel' : '+ Add supplier'}
          </button>
        </div>

        {/* Add Supplier Form */}
        {showAddForm && (
          <form onSubmit={handleAddSupplier} style={{ padding: '1.25rem', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid var(--border)' }}>
            <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, fontSize: '0.9rem', marginBottom: '1rem' }}>Add new supplier</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div>
                <label className="form-label">Supplier name *</label>
                <input className="form-input" type="text" value={form.supplier_name} onChange={(e) => setForm((f) => ({ ...f, supplier_name: e.target.value }))} required />
              </div>
              <div>
                <label className="form-label">Category *</label>
                <select className="form-input" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                  <option value="raw_materials">Raw Materials</option>
                  <option value="packaging">Packaging</option>
                  <option value="logistics">Logistics</option>
                  <option value="services">Services</option>
                </select>
              </div>
              <div>
                <label className="form-label">Annual spend (INR)</label>
                <input className="form-input" type="number" min="0" value={form.annual_spend_inr} onChange={(e) => setForm((f) => ({ ...f, annual_spend_inr: e.target.value }))} />
              </div>
              <div>
                <label className="form-label">Est. Scope 3 (tCO2e)</label>
                <input className="form-input" type="number" min="0" step="any" value={form.estimated_scope3_tco2e} onChange={(e) => setForm((f) => ({ ...f, estimated_scope3_tco2e: e.target.value }))} />
              </div>
              <div>
                <label className="form-label">Emissions score (0-100)</label>
                <input className="form-input" type="number" min="0" max="100" value={form.emissions_score} onChange={(e) => setForm((f) => ({ ...f, emissions_score: e.target.value }))} />
              </div>
              <div>
                <label className="form-label">Data visibility</label>
                <select className="form-input" value={form.scope3_visibility} onChange={(e) => setForm((f) => ({ ...f, scope3_visibility: e.target.value }))}>
                  <option value="none">No disclosure</option>
                  <option value="partial">Partial data</option>
                  <option value="full">Full emissions data</option>
                </select>
              </div>
              <div>
                <label className="form-label">Contact email</label>
                <input className="form-input" type="email" value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} />
              </div>
            </div>
            {error && <div style={{ color: 'var(--neon-pink)', fontFamily: 'Space Mono', fontSize: '0.72rem', marginBottom: '0.5rem' }}>{error}</div>}
            {success && <div style={{ color: 'var(--neon-green)', fontFamily: 'Space Mono', fontSize: '0.72rem', marginBottom: '0.5rem' }}>{success}</div>}
            <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>{submitting ? 'Adding…' : 'Add supplier'}</button>
          </form>
        )}

        {loading ? <div className="stat-sub">Loading…</div> : suppliers.length === 0 ? (
          <div className="card" style={{ background: 'rgba(255,255,255,0.02)', padding: '2rem', textAlign: 'center' }}>
            <div className="stat-sub">No suppliers yet. Add your first supplier to start tracking Scope 3 emissions.</div>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Supplier</th><th>Category</th><th>Annual Spend</th><th>Est. Scope 3</th>
                <th>Grade</th><th>Data Visibility</th><th>Questionnaire</th><th></th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map((s) => (
                <tr key={s.id}>
                  <td style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600, color: 'var(--text)' }}>{s.supplier_name}</td>
                  <td style={{ fontSize: '0.78rem', color: 'var(--muted2)', textTransform: 'capitalize' }}>{s.category.replace('_', ' ')}</td>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.75rem' }}>
                    ₹{s.annual_spend_inr?.toLocaleString('en-IN') || '—'}
                  </td>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.8rem', color: 'var(--neon-orange)' }}>
                    {s.estimated_scope3_tco2e} tCO2e
                  </td>
                  <td>
                    <span className="badge" style={{ color: GRADE_COLORS[s.emissions_grade], borderColor: GRADE_COLORS[s.emissions_grade] }}>
                      Grade {s.emissions_grade}
                    </span>
                  </td>
                  <td>
                    <span className="badge" style={{ color: VISIBILITY_COLORS[s.scope3_visibility], borderColor: VISIBILITY_COLORS[s.scope3_visibility], fontSize: '0.6rem', textTransform: 'capitalize' }}>
                      {s.scope3_visibility === 'full' ? '✓ Full data' : s.scope3_visibility === 'partial' ? '~ Partial' : 'No disclosure'}
                    </span>
                  </td>
                  <td>
                    <span className="badge" style={{ color: Q_STATUS_COLORS[s.questionnaire_status], borderColor: Q_STATUS_COLORS[s.questionnaire_status], fontSize: '0.6rem', textTransform: 'capitalize' }}>
                      {s.questionnaire_status}
                    </span>
                  </td>
                  <td>
                    {s.questionnaire_status === 'pending' && (
                      <button type="button" className="btn btn-secondary btn-sm"
                        style={{ fontSize: '0.65rem', color: 'var(--neon-cyan)', borderColor: 'var(--neon-cyan)' }}
                        onClick={() => handleSendQuestionnaire(s.id)}
                        disabled={sendingQ === s.id}>
                        {sendingQ === s.id ? 'Sending…' : 'Send questionnaire'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ── Questionnaire Template ── */}
      <section className="surface-panel" style={{ padding: '1.5rem' }}>
        <div className="section-tag" style={{ marginBottom: '1rem' }}>Supplier questionnaire template</div>
        <p style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '0.85rem', color: 'var(--muted2)', marginBottom: '1rem' }}>
          The following questions are sent to your suppliers to collect primary emissions data. Completing this data significantly improves the accuracy of your Scope 3 reporting.
        </p>
        <div style={{ display: 'grid', gap: '0.6rem' }}>
          {[
            'What is your total annual Scope 1 + Scope 2 GHG emissions (in tCO2e)?',
            'Do you have a formal carbon reduction target? If yes, please describe it.',
            'What percentage of your electricity consumption comes from renewable sources?',
            'Are you currently compliant with CDP, BRSR, GRI, or ISO 14064? If yes, which framework(s)?',
            'Do you have a verified product carbon footprint (PCF) for goods/services supplied to us?',
          ].map((q, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.75rem', alignItems: 'flex-start', padding: '0.75rem', background: 'rgba(255,255,255,0.02)', borderRadius: '6px' }}>
              <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.68rem', color: 'var(--neon-cyan)', minWidth: '1.5rem' }}>Q{i + 1}</span>
              <span style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '0.85rem', color: 'var(--text)' }}>{q}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
